import MDBox from "../../../components/MDBox";
import {observer} from "mobx-react";
import DashboardProjectManagerLayout from "../../../components/DashboardProjectManager";
import {ROUTES} from "../../../services/constants";
import {Box, Card, CircularProgress} from "@mui/material";
import pxToRem from "../../../assets/theme/functions/pxToRem";
import {Form, Formik} from "formik";
import {projectManagerInitialValues, projectManagerValidationRules} from "../../admin/create-user-account/utils";
import * as Yup from "yup";
import FormikInput from "../../../components/FormikInput";
import MDButton from "../../../components/MDButton";
import UserAvatar from "assets/images/blank_avatar.png";
import camera from "assets/icons/camera.svg";
import { useEffect, useState } from "react";
import {showMessage, useApi, useIsMobile} from "../../../services/helpers";
import stripe_logo from "assets/images/stripe.png";
import MDTypography from "../../../components/MDTypography";

let validationSchema = {...projectManagerValidationRules}
delete validationSchema.permission_type

const MyAccount = () => {
  const api = useApi()
  const [loading, setLoading] = useState(true)
  const [initialValues, setInitialValues] = useState(projectManagerInitialValues)
  const [imgPreview, setImgPreview] = useState(UserAvatar);
  const isMobile = useIsMobile();

  // Stripe state for supply managers
  const [stripeEnabled, setStripeEnabled] = useState(null);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const handleUploadFile = (file) => {
    setImgPreview(URL.createObjectURL(file))
  }

  const getMyProfile = () => {
    api.getMyProfile().then(response => {
      if (response.kind === "ok") {
        setInitialValues(response.data)
        if (response.data.profile_picture) {
          setImgPreview(response.data.profile_picture)
        }
        if (response.data.is_supplymanager) {
          // Check if returning from Stripe onboarding
          const urlParams = new URLSearchParams(window.location.search);
          const returningFromStripe = urlParams.get('success') === 'true';
          
          // Check localStorage for disabled status
          const isDisabled = localStorage.getItem("stripe_disabled") === "true";
          
          // If returning from Stripe, trust the success parameter
          if (returningFromStripe) {
            setStripeConnected(true);
            setStripeEnabled(true);
            localStorage.removeItem("stripe_disabled");
          } else {
            // Otherwise use API response
            setStripeEnabled(!isDisabled);
            setStripeConnected(response.data.stripe_onboarding_complete === true);
          }
        }
      }
    }).catch(() => {
      showMessage('Error getting profile')
    }).finally(() => {
      setLoading(false)
    })
  }

  const saveProfile = (data) => {
    setLoading(true);
    const keys = []
    if (initialValues.profile_picture !== data.profile_picture) {
      keys.push(['profile_picture'])
    }
    api.saveMyProfile(data, keys).then((result) => {
      if (result.kind === 'ok') {
        showMessage('Profile updated successfully', 'success')
      } else {
        showMessage()
      }
    })
      .catch(err => showMessage())
      .finally(() => { setLoading(false) })
  }

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      // If onboarding is not complete, redirect to Stripe onboarding
      if (!stripeConnected) {
        const result = await api.getStripeOnboardingLink();
        if (result.kind === "ok") {
          window.location.href = result.data?.url;
        } else {
          showMessage("Failed to get onboarding link", "error");
          setStripeLoading(false);
        }
      } else {
        // If onboarding is complete but was disabled, just re-enable
        const settingsResult = await api.updateStripeSettings({ reminder_dismissed: false });
        if (settingsResult.kind === "ok") {
          setStripeEnabled(true);
          localStorage.removeItem("stripe_disabled");
          showMessage("Stripe payments enabled", "success");
        } else {
          showMessage("Failed to enable Stripe payments", "error");
        }
        setStripeLoading(false);
      }
    } catch (e) {
      showMessage("Something went wrong", "error");
      setStripeLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    setStripeLoading(true);
    try {
      const result = await api.updateStripeSettings({ reminder_dismissed: true });
      if (result.kind === "ok") {
        setStripeEnabled(false);
        localStorage.setItem("stripe_disabled", "true");
        showMessage("Stripe payments disabled", "success");
      } else {
        showMessage("Failed to update Stripe settings", "error");
      }
    } catch (e) {
      showMessage("Something went wrong", "error");
    } finally {
      setStripeLoading(false);
    }
  };

  useEffect(() => {
    getMyProfile();
    
    // Check if returning from Stripe onboarding
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      // Show success message
      showMessage("Stripe onboarding completed! Your account is now connected.", "success");
      
      // Clean up URL after a moment
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 500);
    }
  }, [])

  const renderStripeSection = () => {
    if (!initialValues.is_supplymanager) return null;

    const isConnected = stripeConnected && stripeEnabled !== false;

    return (
      <MDBox
        sx={{
          width: '100%',
          mt: pxToRem(20),
          p: `${pxToRem(14)} ${pxToRem(16)}`,
          borderRadius: pxToRem(10),
          bgcolor: '#f8f9fa',
          border: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: pxToRem(12),
        }}
      >
        {/* Logo + text */}
        <MDBox display="flex" alignItems="center" gap={pxToRem(12)}>
          {/* Circular logo container */}
          <MDBox
            sx={{
              width: pxToRem(42),
              height: pxToRem(42),
              borderRadius: '50%',
              bgcolor: '#6772e5',
              flexShrink: 0,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={stripe_logo}
              alt="Stripe"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </MDBox>

          <MDBox>
            <MDTypography variant="body2" fontWeight="bold" sx={{ color: '#344767', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
              Stripe payments
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: isConnected ? '#4CAF50' : '#f44335', fontWeight: 600 }}>
              {isConnected ? 'Connected' : stripeConnected && !stripeEnabled ? 'Disabled' : 'Not connected'}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Action buttons */}
        <MDBox display="flex" gap={pxToRem(8)} flexShrink={0}>
          {isConnected && stripeEnabled !== false ? (
            <MDButton
              variant="outlined"
              disabled={stripeLoading}
              onClick={handleDisconnectStripe}
              sx={{
                color: '#f44335',
                borderColor: '#f44335',
                fontWeight: 500,
                minWidth: pxToRem(120),
                bgcolor: '#fff',
                '&:hover': { bgcolor: 'rgba(244,67,54,0.05)' },
              }}
            >
              {stripeLoading ? <CircularProgress size={14} color="inherit" /> : 'Disconnect'}
            </MDButton>
          ) : (
            <MDButton
              variant="outlined"
              disabled={stripeLoading}
              onClick={handleConnectStripe}
              sx={{
                color: '#4CAF50',
                borderColor: '#4CAF50',
                fontWeight: 500,
                minWidth: pxToRem(120),
                bgcolor: '#fff',
                '&:hover': { bgcolor: 'rgba(76,175,80,0.05)' },
              }}
            >
              {stripeLoading ? <CircularProgress size={14} color="inherit" /> : 'Connect'}
            </MDButton>
          )}
        </MDBox>
      </MDBox>
    );
  };

  return (
    <DashboardProjectManagerLayout
      title={'My Account'}
      backButton
      backRoute={ROUTES.USER_PROJECT_SELECTOR}
      alignContent={"center"}
    >
      <Card sx={{ minWidth: '700px', p: pxToRem(52), display: "flex", flexDirection: "column", alignItems: "center"}} >
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object().shape(validationSchema)}
          onSubmit={values => saveProfile(values)}
          enableReinitialize
        >
          {(formik) => (
            <Form style={{ display: "flex", flexDirection: "column", alignItems: "center", width: pxToRem(355), gap: 5}}>
              <MDBox sx={{ position: 'relative' }}>
                <Box component={"img"} src={imgPreview} alt={"Profile Picture"} width={pxToRem(145)}
                     height={pxToRem(145)} borderRadius={'50%'} sx={{objectFit: 'cover'}}/>
                <input
                  type='file'
                  accept='image/*'
                  id={'input_file'}
                  onChange={(e) => {
                    const file = e.target.files[0]
                    handleUploadFile(file);
                    formik.setFieldValue('profile_picture', file)
                  }}
                  style={{display: 'none'}}
                />
                <label htmlFor='input_file'>
                  <Box component={"img"} src={camera} alt={"camera"} sx={{ position: 'absolute', bottom: 15, right: 0, cursor: 'pointer'}} />
                </label>
              </MDBox>
              <FormikInput type={"text"} label={"Full Name"} name={"name"} sx={{ width: "100%", mt:2 }}/>
              <FormikInput type={"text"} label={"Email"} name={"email"} sx={{ width: "100%", mt:2 }} InputProps={{ readOnly: true}} />
              <FormikInput type={"text"} label={"Job Title"} name={"title"} sx={{ width: "100%", mt:2 }}/>
              <FormikInput type={"phone_input"} label={"Phone Number"} name={"phone_number"} sx={{ width: "100%", mt:2 }}/>

              {initialValues.is_projectmanager === true && (<>
                <FormikInput type={"text"} label={"Company"} name={"company_name"} sx={{ width: "100%", mt:2 }} disabled />
                <FormikInput type={"text"} label={"Region"} name={"region_name"} sx={{ width: "100%", mt:2 }} disabled />
              </>)}
              {initialValues.is_supplymanager === true && (<>
                <FormikInput type={"text"} label={"Manufacturer"} name={"manufacturer_name"} sx={{ width: "100%", mt:2 }} disabled />
              </>)}

              {renderStripeSection()}

              <MDButton type={"submit"} color={"primary"} sx={{ width: pxToRem(288), mt: pxToRem(20) }} loading={loading} disabled={loading}>
                Save
              </MDButton>
            </Form>
          )}
        </Formik>
      </Card>
    </DashboardProjectManagerLayout>
  );
}

export default observer(MyAccount);
